import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { OnboardingService } from '../../core/services/onboarding.service';
import { ToastService } from '../../core/services/toast.service';

@Component({
  selector: 'app-onboard-user',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div
      class="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden font-sans bg-cover bg-center"
      [style.backgroundImage]="'url(/img/zanzibar.jpg)'"
    >
      <!-- Backdrop Overlay -->
      <div class="absolute inset-0 bg-slate-950/60 backdrop-blur-[3px] z-0"></div>

      <div class="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <!-- Official Government Seal & Brand -->
        <div class="flex flex-col items-center gap-3">
          <div class="flex items-center gap-3 bg-white/95 px-5 py-2.5 rounded-full border border-slate-200 shadow-lg backdrop-blur">
            <img src="/img/smz.png" class="w-10 h-auto" alt="SMZ Government Seal" />
            <div class="h-6 w-px bg-slate-200"></div>
            <div class="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center font-black text-white text-xs">ZX</div>
          </div>
        </div>

        <h2 class="mt-6 text-center text-2xl font-black text-white tracking-tight drop-shadow-md">
          Institution Account Activation
        </h2>
        <p class="mt-1 text-center text-xs text-emerald-200 font-extrabold tracking-wider uppercase drop-shadow-sm">
          Zan-X Change &bull; Onboarding Verification
        </p>
      </div>

      <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-lg relative z-10">
        <div class="bg-white/95 p-8 rounded-3xl shadow-2xl border border-slate-200 backdrop-blur-md">
          
          <!-- STATE 1: SETUP FORM -->
          @if (!isSuccess()) {
            <form (ngSubmit)="onSubmit()" class="space-y-5">
              <div class="text-center pb-2 border-b border-slate-200">
                <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-bold mb-2">
                  <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  Official Registration Link Verified
                </div>
                <h3 class="text-base font-bold text-slate-900">Set Administrator Password</h3>
                <p class="text-xs text-slate-500 mt-1">
                  Welcome to Zan-X Change. Your onboarding invitation token has been detected. Please create your institutional administrator credentials.
                </p>
              </div>

              <!-- Token Display Pill -->
              <div class="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                <div class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Invitation / Setup Token
                </div>
                <div class="flex items-center justify-between font-mono text-xs font-bold text-slate-800 break-all bg-white px-3 py-2 rounded-lg border border-slate-200">
                  <span class="truncate">{{ token || 'No Token Detected' }}</span>
                  <span class="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 shrink-0 ml-2 font-sans font-bold">
                    ACTIVE
                  </span>
                </div>
              </div>

              @if (errorMessage()) {
                <div class="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
                  <svg class="w-4 h-4 text-rose-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{{ errorMessage() }}</span>
                </div>
              }

              <!-- New Password Field -->
              <div>
                <label for="onboard-password" class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  New Administrator Password
                </label>
                <div class="relative">
                  <input
                    id="onboard-password"
                    [type]="showPassword() ? 'text' : 'password'"
                    [(ngModel)]="newPassword"
                    name="newPassword"
                    required
                    minlength="8"
                    placeholder="Create a strong password (min 8 characters)"
                    class="w-full px-3.5 py-2.5 pr-10 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  />
                  <button
                    type="button"
                    (click)="showPassword.set(!showPassword())"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    @if (showPassword()) {
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                      </svg>
                    } @else {
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    }
                  </button>
                </div>
              </div>

              <!-- Confirm Password Field -->
              <div>
                <label for="onboard-confirm-password" class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Confirm Password
                </label>
                <div class="relative">
                  <input
                    id="onboard-confirm-password"
                    [type]="showConfirmPassword() ? 'text' : 'password'"
                    [(ngModel)]="confirmPassword"
                    name="confirmPassword"
                    required
                    placeholder="Confirm your password"
                    class="w-full px-3.5 py-2.5 pr-10 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                    [class.border-rose-400]="confirmPassword && newPassword !== confirmPassword"
                    [class.border-emerald-400]="confirmPassword && newPassword === confirmPassword"
                  />
                  <button
                    type="button"
                    (click)="showConfirmPassword.set(!showConfirmPassword())"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    @if (showConfirmPassword()) {
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                      </svg>
                    } @else {
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    }
                  </button>
                </div>
                @if (confirmPassword && newPassword !== confirmPassword) {
                  <p class="text-[11px] text-rose-600 font-semibold mt-1">Passwords do not match</p>
                }
              </div>

              <!-- Validation Checklist -->
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200 text-[11px] space-y-1 text-slate-600">
                <div class="font-bold text-slate-700 mb-1">Security Criteria:</div>
                <div class="flex items-center gap-1.5" [class.text-emerald-700]="newPassword.length >= 8">
                  <span>{{ newPassword.length >= 8 ? '✓' : '○' }}</span>
                  <span>Minimum 8 characters</span>
                </div>
                <div class="flex items-center gap-1.5" [class.text-emerald-700]="confirmPassword && newPassword === confirmPassword">
                  <span>{{ confirmPassword && newPassword === confirmPassword ? '✓' : '○' }}</span>
                  <span>Passwords match exactly</span>
                </div>
              </div>

              <!-- Submit Button -->
              <button
                type="submit"
                [disabled]="loading() || !canSubmit()"
                class="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-sm shadow-md shadow-emerald-600/20 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                @if (loading()) {
                  <span class="inline-flex items-center gap-2">
                    <svg class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving Credentials & Activating...
                  </span>
                } @else {
                  Complete Setup & Set Password &rarr;
                }
              </button>

              <div class="pt-2 border-t border-slate-200 text-center text-xs text-slate-600">
                Already established your credentials?
                <a routerLink="/login" class="font-bold text-emerald-700 hover:text-emerald-800 hover:underline pl-1">
                  Sign In to Portal
                </a>
              </div>
            </form>
          } @else {
            <!-- STATE 2: CONGRATULATIONS / SUCCESS -->
            <div class="text-center space-y-5">
              <div class="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto text-white shadow-lg shadow-emerald-500/20">
                <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <div>
                <h3 class="text-xl font-black text-slate-900">Account Credentials Established!</h3>
                <p class="text-xs text-slate-600 mt-2 leading-relaxed">
                  Your administrator security password has been configured successfully.
                </p>
              </div>

              <!-- Notice Box -->
              <div class="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-left text-xs space-y-1.5 shadow-2xs">
                <div class="font-bold flex items-center gap-2 text-amber-800">
                  <svg class="w-4 h-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Institutional Verification Notice
                </div>
                <p class="text-[11px] leading-relaxed text-amber-800/90">
                  If this account belongs to a newly registered institution, official platform access will be activated immediately upon Super Administrator review and approval. You will receive an email confirmation once verified.
                </p>
              </div>

              <div class="pt-2">
                <a
                  routerLink="/login"
                  class="w-full block py-3.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-sm shadow-md shadow-emerald-600/20 text-center transition-all"
                >
                  Proceed to Login &rarr;
                </a>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  `
})
export class OnboardUserComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private onboardingService = inject(OnboardingService);
  private toastService = inject(ToastService);

  token = '';
  newPassword = '';
  confirmPassword = '';

  showPassword = signal<boolean>(false);
  showConfirmPassword = signal<boolean>(false);
  loading = signal<boolean>(false);
  isSuccess = signal<boolean>(false);
  errorMessage = signal<string>('');

  ngOnInit() {
    // Extract token from route param (:token) or query param (?token=)
    const routeToken = this.route.snapshot.paramMap.get('token');
    const queryToken = this.route.snapshot.queryParamMap.get('token');

    this.token = (routeToken || queryToken || '').trim();

    if (!this.token) {
      this.errorMessage.set('No invitation token found in link. Please click the complete setup link received in your registration email.');
    }
  }

  canSubmit(): boolean {
    return !!(
      this.token &&
      this.newPassword.length >= 8 &&
      this.newPassword === this.confirmPassword
    );
  }

  onSubmit() {
    if (!this.canSubmit()) return;

    this.loading.set(true);
    this.errorMessage.set('');

    this.onboardingService.createInitialUser({
      token: this.token,
      password: this.newPassword
    }).subscribe({
      next: () => {
        this.loading.set(false);
        this.isSuccess.set(true);
        this.toastService.success('Initial administrator credentials established successfully!');
      },
      error: (err) => {
        this.loading.set(false);
        const msg = err.error?.message || 'Failed to complete account setup. The invitation token may be invalid or expired. Please contact support.';
        this.errorMessage.set(msg);
        this.toastService.error(msg);
      }
    });
  }
}
